/**
 * Display helpers for entities with populated `suites[]` or legacy `suite`
 * (Lead, SmartProposal, SmartSuiteAgreement, list rows, etc.).
 */

export function formatSuiteNumbersList(row) {
  if (!row) return "";
  const suites = row.suites;
  if (Array.isArray(suites) && suites.length > 0) {
    const parts = suites
      .map((s) => {
        if (s == null) return "";
        if (
          typeof s === "object" &&
          s.suiteNumber != null &&
          String(s.suiteNumber).trim() !== ""
        ) {
          return String(s.suiteNumber);
        }
        return "";
      })
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "";
  }
  const su = row.suite;
  if (
    su &&
    typeof su === "object" &&
    su.suiteNumber != null &&
    String(su.suiteNumber).trim() !== ""
  ) {
    return String(su.suiteNumber);
  }
  return "";
}

export function formatSuiteNumbersDisplay(row) {
  const s = formatSuiteNumbersList(row);
  return s || "N/A";
}

export function sumDefaultSeatsFromSuites(row) {
  if (!row) return 0;
  const suites = row.suites;
  if (Array.isArray(suites) && suites.length > 0) {
    return suites.reduce((a, s) => {
      if (s && typeof s === "object") {
        const n = Number(s.defaultSeats);
        return a + (Number.isFinite(n) ? n : 0);
      }
      return a;
    }, 0);
  }
  if (row.suite && typeof row.suite === "object") {
    const n = Number(row.suite.defaultSeats);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Seat count for read-only headers: sum from suites, else noOfPeople / legacy suite. */
export function formatAgreementSeatCountDisplay(row) {
  if (!row) return "";
  const fromSuites = sumDefaultSeatsFromSuites(row);
  if (fromSuites > 0) return fromSuites;
  if (row.noOfPeople != null && row.noOfPeople !== "")
    return row.noOfPeople;
  if (row.suite && typeof row.suite === "object")
    return row.suite.defaultSeats ?? "";
  return "";
}

/**
 * Short floor / business-center label from a populated suite subdoc.
 */
function floorLabelFromSuiteBusinessCenter(suite) {
  if (!suite || typeof suite !== "object") return "";
  const bc = suite.businessCenter;
  if (bc && typeof bc === "object" && bc.name != null && String(bc.name).trim() !== "") {
    return String(bc.name).split("-")[0].trim();
  }
  return "";
}

/** Stable key for deduping floors (BC id or label fallback). */
function businessCenterKeyFromSuite(suite) {
  if (!suite || typeof suite !== "object") return "";
  const bc = suite.businessCenter;
  if (bc && typeof bc === "object" && bc._id != null) return String(bc._id);
  if (bc) return String(bc);
  const lbl = floorLabelFromSuiteBusinessCenter(suite);
  return lbl ? `n:${lbl}` : "";
}

/**
 * Lead / detail drawer: all distinct floors from populated `suites[]` (or legacy `suite`).
 * Same physical floor → one label; different floors → comma-separated.
 * Returns null when suite refs are not populated enough to infer multiple floors (caller may fall back).
 */
export function formatFloorsDisplayFromSuites(row) {
  if (!row) return null;
  const entries = [];
  const pushSuite = (suite) => {
    if (!suite || typeof suite !== "object") return;
    const key = businessCenterKeyFromSuite(suite);
    const label = floorLabelFromSuiteBusinessCenter(suite);
    if (!key && !label) return;
    entries.push({ key: key || `n:${label}`, label });
  };
  if (Array.isArray(row.suites) && row.suites.length > 0) {
    row.suites.forEach(pushSuite);
  } else if (row.suite && typeof row.suite === "object") {
    pushSuite(row.suite);
  }
  if (entries.length === 0) return null;
  const byKey = new Map();
  for (const e of entries) {
    if (!byKey.has(e.key)) byKey.set(e.key, e.label);
    else if (!byKey.get(e.key) && e.label) byKey.set(e.key, e.label);
  }
  const keys = [...byKey.keys()];
  const labels = keys.map((k) => byKey.get(k)).filter((l) => l && String(l).trim() !== "");
  if (labels.length === keys.length && labels.length > 0) {
    const unique = [...new Set(labels)];
    return unique.length === 1 ? unique[0] : unique.join(", ");
  }
  if (labels.length > 0) {
    const unique = [...new Set(labels)];
    const unnamed = keys.length - labels.length;
    const base = unique.length === 1 ? unique[0] : unique.join(", ");
    return unnamed > 0 ? `${base} (+${unnamed} more)` : base;
  }
  if (keys.length > 1) return "Multiple floors";
  return null;
}

function floorLabelForLeadSuiteRow(suite, row) {
  const fromBc = floorLabelFromSuiteBusinessCenter(suite);
  if (fromBc) return fromBc;
  if (row?.floor?.name) return String(row.floor.name).split("-")[0].trim();
  return getAgreementFloorDisplayName(row) || "";
}

/**
 * Lead detail — Client Requirement: one line per suite:
 * `Floor {name} - Suite {number} - {n} Seats`
 * Newline-separated when multiple. Falls back to one line if only suite numbers are known.
 */
export function formatLeadClientRequirementSuiteLines(row) {
  if (!row) return "";
  const lines = [];
  const pushFromSuite = (suite) => {
    if (!suite || typeof suite !== "object") return;
    const sn =
      suite.suiteNumber != null && String(suite.suiteNumber).trim() !== ""
        ? String(suite.suiteNumber).trim()
        : "";
    if (!sn) return;
    const fl = floorLabelForLeadSuiteRow(suite, row) || "—";
    const ds = suite.defaultSeats;
    let seats = "—";
    if (ds != null && String(ds).trim() !== "") {
      const n = Number(ds);
      seats = Number.isFinite(n) ? n : String(ds).trim();
    }
    lines.push(`${fl} - Suite ${sn} - ${seats} Seats`);
  };
  if (Array.isArray(row.suites) && row.suites.length > 0) {
    row.suites.forEach(pushFromSuite);
  } else if (row.suite && typeof row.suite === "object") {
    pushFromSuite(row.suite);
  }
  if (lines.length > 0) return lines.join("\n");
  const nums = formatSuiteNumbersList(row);
  if (!nums) return "";
  const fl =
    (row.suite && typeof row.suite === "object"
      ? floorLabelForLeadSuiteRow(row.suite, row)
      : "") ||
    (row.floor?.name ? String(row.floor.name).split("-")[0].trim() : "") ||
    getAgreementFloorDisplayName(row) ||
    "—";
  const seatTotal =
    sumDefaultSeatsFromSuites(row) ||
    (row.workstations != null && row.workstations !== ""
      ? row.workstations
      : "");
  const seats = seatTotal !== "" && seatTotal != null ? seatTotal : "—";
  return `${fl} - Suite ${nums} - ${seats} Seats`;
}

/**
 * Floor / business-center label: prefer first populated suite's BC, else legacy suite, else floor.
 */
export function getAgreementFloorDisplayName(agreement) {
  if (!agreement) return "";
  let suiteRef = agreement.suite;
  if (Array.isArray(agreement.suites) && agreement.suites.length > 0) {
    const first = agreement.suites[0];
    if (first && typeof first === "object") suiteRef = first;
  }
  const bcFromSuite = suiteRef?.businessCenter;
  const nameFromSuite =
    bcFromSuite && typeof bcFromSuite === "object" && bcFromSuite.name
      ? bcFromSuite.name
      : null;
  const nameFromFloor = agreement.floor?.name;
  const raw = nameFromSuite || nameFromFloor;
  if (!raw) return "";
  return String(raw).split("-")[0];
}

/**
 * Full floor / business-center name (NOT truncated at the first "-").
 * Same source preference as getAgreementFloorDisplayName, but keeps the entire
 * name. Used by the Linked Agreement table which needs the complete floor name.
 */
export function getAgreementFloorFullName(agreement) {
  if (!agreement) return "";
  let suiteRef = agreement.suite;
  if (Array.isArray(agreement.suites) && agreement.suites.length > 0) {
    const first = agreement.suites[0];
    if (first && typeof first === "object") suiteRef = first;
  }
  const bcFromSuite = suiteRef?.businessCenter;
  const nameFromSuite =
    bcFromSuite && typeof bcFromSuite === "object" && bcFromSuite.name
      ? bcFromSuite.name
      : null;
  const nameFromFloor = agreement.floor?.name;
  const raw = nameFromSuite || nameFromFloor;
  if (!raw) return "";
  return String(raw).trim();
}

/**
 * Agreement detail: comma-separated distinct business center names from populated `suites[]` / `suite`.
 * Falls back to `agreement.floor.name` when suite BCs are not populated.
 */
export function formatAgreementBusinessCentersDisplay(agreement) {
  if (!agreement || typeof agreement !== "object" || Array.isArray(agreement)) {
    return "";
  }
  const names = [];
  const pushSuite = (suite) => {
    if (!suite || typeof suite !== "object") return;
    const bc = suite.businessCenter;
    if (
      bc &&
      typeof bc === "object" &&
      bc.name != null &&
      String(bc.name).trim() !== ""
    ) {
      names.push(String(bc.name).trim());
    }
  };
  if (Array.isArray(agreement.suites) && agreement.suites.length > 0) {
    agreement.suites.forEach(pushSuite);
  }
  if (agreement.suite && typeof agreement.suite === "object") {
    pushSuite(agreement.suite);
  }
  const unique = [...new Set(names)];
  if (unique.length > 0) return unique.join("<br />");
  if (agreement.floor?.name) return String(agreement.floor.name);
  return "";
}

/**
 * Rows for a read-only suite table (e.g. client agreement detail drawer).
 */
export function buildAgreementSuiteTableRows(agreement) {
  if (!agreement || typeof agreement !== "object" || Array.isArray(agreement)) {
    return [];
  }
  const rows = [];
  const add = (suite) => {
    if (!suite || typeof suite !== "object") return;
    const bc = suite.businessCenter;
    const bcName =
      bc &&
        typeof bc === "object" &&
        bc.name != null &&
        String(bc.name).trim() !== ""
        ? String(bc.name).trim()
        : "N/A";
    rows.push({
      key: suite._id
        ? String(suite._id)
        : `${bcName}-${suite.suiteNumber}-${rows.length}`,
      businessCenter: bcName,
      suiteNumber:
        suite.suiteNumber != null && String(suite.suiteNumber).trim() !== ""
          ? String(suite.suiteNumber)
          : "N/A",
      defaultSeats:
        suite.defaultSeats != null && suite.defaultSeats !== ""
          ? suite.defaultSeats
          : "N/A",
      status: suite.status || "N/A",
    });
  };
  if (Array.isArray(agreement.suites) && agreement.suites.length > 0) {
    agreement.suites.forEach(add);
  } else if (agreement.suite && typeof agreement.suite === "object") {
    add(agreement.suite);
  }
  return rows;
}

function floorLabelForAgreementSuite(suite, agreement) {
  const fromBc = floorLabelFromSuiteBusinessCenter(suite);
  if (fromBc) return fromBc;
  if (agreement?.floor?.name)
    return String(agreement.floor.name).split("-")[0].trim();
  return getAgreementFloorDisplayName(agreement) || "";
}

/**
 * Agreement list: one string per suite, e.g. "Level 1 - 101" (floor label from BC / agreement.floor).
 */
export function formatAgreementFloorSuiteLines(row) {
  if (!row) return [];
  const lines = [];
  const pushFromSuite = (suite) => {
    if (!suite || typeof suite !== "object") return;
    const sn =
      suite.suiteNumber != null && String(suite.suiteNumber).trim() !== ""
        ? String(suite.suiteNumber).trim()
        : "";
    if (!sn) return;
    const fl = floorLabelForAgreementSuite(suite, row) || "—";
    lines.push(`${fl} - Suite ${sn}`);
  };
  if (Array.isArray(row.suites) && row.suites.length > 0) {
    row.suites.forEach(pushFromSuite);
  } else if (row.suite && typeof row.suite === "object") {
    pushFromSuite(row.suite);
  }
  if (lines.length > 0) return lines;
  const nums = formatSuiteNumbersList(row);
  if (!nums) return [];
  const fl =
    (row.floor?.name ? String(row.floor.name).split("-")[0].trim() : "") ||
    getAgreementFloorDisplayName(row) ||
    "—";
  return nums
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((p) => `${fl} - ${p}`);
}

export function formatAgreementFloorSuiteDisplayPlain(row) {
  const lines = formatAgreementFloorSuiteLines(row);
  return lines.length ? lines.join("\n") : "N/A";
}

/**
 * Occupancy Tracker company view: rows use `floorName` and comma-separated `suiteNumber`.
 * Delegates to agreement-style lines when `suites` / `suite` exist on the row.
 */
export function formatOccupancyTrackerFloorSuiteLines(row) {
  if (!row) return [];
  const agreementLines = formatAgreementFloorSuiteLines(row);
  if (agreementLines.length) return agreementLines;
  const floorShort =
    row.floorName != null && String(row.floorName).trim() !== ""
      ? String(row.floorName).split("-")[0].trim()
      : "";
  const sn = row.suiteNumber;
  if (sn == null || String(sn).trim() === "") return [];
  const fl = floorShort || "—";
  return String(sn)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((n) => `${fl} - Suite ${n}`);
}

export function formatOccupancyTrackerFloorSuiteDisplayPlain(row) {
  const lines = formatOccupancyTrackerFloorSuiteLines(row);
  return lines.length ? lines.join("\n") : "N/A";
}

/** Proposal / PDF / public view: "Suite - 101, 102" or "Suite" when unknown. */
export function formatSuiteSectionTitle(row) {
  const nums = formatSuiteNumbersList(row);
  return nums ? `Suite - ${nums}` : "Suite";
}

/**
 * Lead / detail one-line: e.g. "101 - 10 seats (Vacant); 102 - 8 seats"
 */
export function formatSuitesDetailWithSeats(row) {
  if (!row) return "";
  const suites = row.suites;
  if (Array.isArray(suites) && suites.length > 0) {
    const parts = suites
      .map((s) => {
        if (!s || typeof s !== "object") return "";
        if (s.suiteNumber == null || String(s.suiteNumber).trim() === "")
          return "";
        const ds = s.defaultSeats;
        const st = s.status;
        return `${s.suiteNumber} - ${ds ?? ""} seats${st ? ` (${st})` : ""}`;
      })
      .filter(Boolean);
    return parts.length ? parts.join("; ") : "";
  }
  const su = row.suite;
  if (su && typeof su === "object" && su.suiteNumber != null) {
    return `${su.suiteNumber} - ${su.defaultSeats ?? ""} seats${su.status ? ` (${su.status})` : ""
      }`;
  }
  return "";
}
